import React from 'react';
import {
	Box,
	Modal as ChakraModal,
	HStack,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Text,
	VStack,
} from '@chakra-ui/react';
import { RemoveScroll } from 'react-remove-scroll';
import { Button } from 'newui/components/Button';
import { ModalProps } from './Modal.types';
import { FiAlertCircle, FiAlertTriangle, FiCheckCircle, FiDownload, FiEdit, FiInfo, FiMail, FiPlusCircle, FiSend } from 'react-icons/fi';
import { cn } from 'utils/helpers';

const Modal: React.FC<ModalProps> = ({
	isOpen,
	initialFocusRef,
	description,
	headerAccessory,
	onClose,
	title,
	hideCloseButton = false,
	iconType = 'warning',
	customIcon,
	fillBody = false,
	iconBgColor,
	iconColor,
	titleColor,
	children,
	primaryButton,
	secondaryButton,
	secondaryButtonFirst = false,
	customFooter,
	closeOnOverlayClick = false,
	closeOnEsc = false,
	size = 'lg',
	isCentered = true,
	modalType = 'standard',
	titleSize = '2xl',
	trapFocus = true,
	wrapDescription = false
}) => {
	const isStandardModal = modalType === 'standard';
	const getIconConfig = () => {
		if (customIcon) {
			return { icon: customIcon, bgColor: iconBgColor || 'gray.50', color: iconColor || 'gray.500' };
		}

		switch (iconType) {
			case 'create':
				return {
					icon: <FiPlusCircle size={30} className='fill-white stroke-blue-500' />,
				};
			case 'download':
				return {
					icon: <FiDownload size={40} className='fill-blue-200 stroke-blue-500' />,
				};
			case 'edit':
				return {
					icon: <FiEdit size={30} className='fill-white stroke-blue-500' />,
				};
			case 'error':
				return {
					icon: <FiAlertCircle size={40} className='fill-red-200 stroke-red-700' />,
				};
			case 'info':
				return {
					icon: <FiInfo size={40} className='fill-blue-200 stroke-blue-500' />,
				};
			case 'mail':
				return {
					icon: <FiMail size={40} className='fill-blue-200 stroke-blue-500' />,
				};
			case 'success':
				return {
					icon: <FiCheckCircle size={40} className='fill-blue-200 stroke-blue-600' />
				};
			case 'warning':
				return {
					icon: <FiAlertTriangle size={40} className='fill-yellow-500 stroke-black' />,
				};
			default:
				return {
					icon: null,
					bgColor: 'black.50',
					color: 'black.50',
				};
		}
	};

	const getTitleColor = () => {
		if (titleColor) {
			return titleColor;
		}

		if (customIcon) {
			return 'gray.800';
		}

		switch (iconType) {
			case 'error':
				return 'red.700';
			case 'warning':
				return 'yellow.600';
			case 'info':
			case 'mail':
				return 'blue.500';
			case 'success':
				return 'blue.600';
			default:
				return 'gray.800';
		}
	};

	const iconConfig = getIconConfig();
	const computedTitleColor = getTitleColor();

	const maxWidthMap: Record<string, string | undefined> = {
		sm: '400px',
		md: '500px',
		lg: '600px',
		xl: '800px',
		xxl: '1200px',
		'6xl': undefined,
		full: undefined,
	};

	const isFullSize = size === 'full' || size === '6xl';

	// A body with a definite height, so a `flex-1 min-h-0 overflow-auto` child can
	// own the scrolling instead of adding a second scroll region alongside the
	// body's own (PRO-718). Full-size modals already get this treatment.
	const bodyFills = isFullSize || fillBody;

	const titleIsText = typeof title === 'string';

	const renderHeader = () => {
		return modalType === 'alert' ? <ModalHeader
			pt={8}
			pb={4}
			px={6}
			borderBottom="1px solid"
			borderColor="gray.100"
		>
			<VStack spacing={3} align="center" className='flex items-center'>
				<Box borderRadius="full" bg={iconConfig.bgColor}>
					{React.isValidElement(iconConfig.icon) ? (
						React.cloneElement(iconConfig.icon as React.ReactElement, {
							stroke: iconConfig.color,
						})
					) : (
						iconConfig.icon
					)}
				</Box>
				{titleIsText ? <Text fontSize={titleSize} fontWeight="bold" color={computedTitleColor} textAlign="center">
					{title}
				</Text> : title}
				{description && <Text>
					{description}
				</Text>}
			</VStack>
		</ModalHeader> : <ModalHeader
			borderBottom="1px solid"
			borderColor="gray.100"
		>
			<VStack align="stretch" spacing={headerAccessory ? 3 : 0} w="full">
				<HStack align={"left"} spacing={5} className='flex items-center'>
					<Box borderRadius="full" bg={iconConfig.bgColor}>
						{React.isValidElement(iconConfig.icon) ? (
							React.cloneElement(iconConfig.icon as React.ReactElement, {
								stroke: iconConfig.color,
							})
						) : (
							iconConfig.icon
						)}
					</Box>
					<div className={cn("flex text-left gap-0 items-center"
						, wrapDescription ? 'flex-row' : 'flex-col'
					)}>

						<Text fontSize={titleSize} fontWeight="bold" color={computedTitleColor}>
							{title}
						</Text>
						{description && (typeof description === "string" ? <Text fontSize="md" fontWeight="normal" marginTop={wrapDescription ? 1 : 0} marginLeft={wrapDescription ? 4 : 0}>
							{description}
						</Text>
							: description)}
					</div>
				</HStack>
				{headerAccessory && <Box w="full">{headerAccessory}</Box>}
			</VStack>
		</ModalHeader>

	}

	const renderFooter = () => {
		if (customFooter) {
			return customFooter;
		}

		if (!primaryButton && !secondaryButton) {
			return null;
		}

		const primaryButtonNode = primaryButton && (
			<Button
				key="primary"
				onClick={primaryButton.onClick}
				btntype={primaryButton.btntype || 'primary'}
				size={primaryButton.size ?? "sm"}
				width={primaryButton.width ?? (isStandardModal ? "100px" : undefined)}
				disabled={primaryButton.disabled}
				isLoading={primaryButton.isLoading}
				className={primaryButton.className}
			>
				<span className={primaryButton.icon && 'pr-2'}>{primaryButton.icon}</span>
				{primaryButton.text}
			</Button>
		);

		const secondaryButtonNode = secondaryButton && (
			<Button
				key="secondary"
				onClick={secondaryButton.onClick}
				btntype={secondaryButton.btntype || 'secondary'}
				size={secondaryButton.size ?? "sm"}
				width={secondaryButton.width ?? (isStandardModal ? "100px" : undefined)}
				disabled={secondaryButton.disabled}
				isLoading={secondaryButton.isLoading}
				className={secondaryButton.className}
			>
				<span className={secondaryButton.icon && 'pr-2'}>{secondaryButton.icon}</span>
				{secondaryButton.text}
			</Button>
		);

		return (
			<div className={isStandardModal ? "flex w-full justify-end gap-2" : "flex w-full justify-center gap-3"}>
				{secondaryButtonFirst ? (
					<>
						{secondaryButtonNode}
						{primaryButtonNode}
					</>
				) : (
					<>
						{primaryButtonNode}
						{secondaryButtonNode}
					</>
				)}
			</div>
		);
	};

	return (
		<ChakraModal
			isOpen={isOpen}
			onClose={onClose}
			initialFocusRef={initialFocusRef as React.RefObject<HTMLElement>}
			isCentered={isCentered}
			closeOnOverlayClick={closeOnOverlayClick}
			closeOnEsc={closeOnEsc}
			trapFocus={trapFocus}
			motionPreset="scale"
			size={size}
			// 'inside' at every size so a tall body scrolls within the modal (header/
			// footer stay fixed) instead of overflowing off-screen with no way to reach
			// it — previously only full-size modals were scrollable.
			scrollBehavior="inside"
		>
			<ModalOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
			{/*
				PRO-728 (reopened) — a Modal opened OVER an already-open Drawer (e.g.
				Edit user > View access) could not be scrolled with a mouse wheel.
				Chakra's Drawer is built on the same Modal primitive as this Modal and
				both share ONE global `modalManager` singleton that only enables
				react-remove-scroll for the FIRST dialog registered with it — a Modal
				opened on top of an already-open Drawer gets disqualified from ever
				locking/owning scroll itself. react-remove-scroll's own wheel listener
				then defers entirely to whichever lock mounted first (the Drawer's),
				and since the Modal's DOM is a separate portal — not a descendant of
				the Drawer's locked content and not a registered `shard` — every wheel
				event inside the Modal gets treated as "outside" the lock and is
				cancelled (dragging the scrollbar still worked, which is why only the
				wheel was reported broken).
				Wrapping ModalContent in our own RemoveScroll (unconditionally, i.e.
				not gated by Chakra's index check) makes this Modal always become the
				active/topmost scroll lock while it is open, taking over from
				whatever lock (a Drawer, another Modal) was already active, while
				still isolating/blocking background scroll like before (PRO-718).
			*/}
			<RemoveScroll forwardProps removeScrollBar={false} allowPinchZoom={false}>
				<ModalContent
					borderRadius="xl"
					bg="white"
					boxShadow="2xl"
					maxW={maxWidthMap[size]}
					mx="auto"
					{...(isFullSize && { maxH: '95vh', display: 'flex', flexDirection: 'column' })}
					{...(!isFullSize && fillBody && { maxH: '85vh', display: 'flex', flexDirection: 'column' })}
				>
					{!hideCloseButton && <ModalCloseButton className="z-[999] !right-2 !top-2" size={"md"} />}
					{renderHeader()}

					<ModalBody
						px={6}
						pt={6}
						pb={2}
						{...(isFullSize && { flex: 1, overflowY: 'auto' })}
						{...(bodyFills && { minH: 0, display: 'flex', flexDirection: 'column' })}
						{...(!isFullSize && fillBody && { flex: 1, overflowY: 'auto' })}
					>
						<VStack spacing={6} align="stretch" {...(bodyFills && { flex: 1, minH: 0 })}>
							{children}
						</VStack>
					</ModalBody>

					{(customFooter || primaryButton || secondaryButton) && (
						<ModalFooter px={6} pb={4} pt={2} flexDirection="column" gap={3}>
							{renderFooter()}
						</ModalFooter>
					)}
				</ModalContent>
			</RemoveScroll>
		</ChakraModal>
	);
};

export default Modal;
